import { UcoastEl } from '@/scripts/core/UcoastEl'
import { TsDOM as q } from '@/scripts/core/TsDOM';

export class MarqueeSlider extends UcoastEl {
    static htmlSelector = 'marquee-slider'
    private textWrapper: HTMLElement
    private rail: HTMLElement | undefined
    private toggle: HTMLButtonElement | undefined
    private copyStarts: HTMLElement[]
    private speed: number
    private animation: number | null
    private currentPosition = 0
    private observer: IntersectionObserver
    private resizeObserver: ResizeObserver
    private motionPreference: MediaQueryList
    private loopWidth = 0
    private isIntersecting = false
    private paused = false
    private drag?: { pointerId: number; x: number; y: number; position: number; active: boolean }
    private suppressClick = false

    constructor() {
        super()
        this.textWrapper = q.rs('[data-uc-marquee-wrapper]', this)
        this.rail = q.os('[data-uc-marquee-rail]', this)
        this.toggle = q.os<HTMLButtonElement>('[data-uc-marquee-toggle]', this)
        this.copyStarts = Array.from(
            q.ol('[data-uc-marquee-copy-start]', this) ?? []
        )
        this.speed = Number(this.dataset.speed) / 10 || 0.2
        this.animation = null
        this.motionPreference = window.matchMedia('(prefers-reduced-motion: reduce)')

        this.handleToggle = this.handleToggle.bind(this)
        this.handleFocusIn = this.handleFocusIn.bind(this)
        this.handleMotionPreference = this.handleMotionPreference.bind(this)
        this.updateLoopWidth = this.updateLoopWidth.bind(this)
        this.handlePointerDown = this.handlePointerDown.bind(this)
        this.handlePointerMove = this.handlePointerMove.bind(this)
        this.handlePointerEnd = this.handlePointerEnd.bind(this)
        this.handleClick = this.handleClick.bind(this)
        this.handleDragStart = this.handleDragStart.bind(this)

        this.resizeObserver = new ResizeObserver(this.updateLoopWidth)
        this.observer = new IntersectionObserver(this.handleIntersection.bind(this), {
            threshold: 0,
        })
    }

    override connectedCallback(): void {
        super.connectedCallback()
        this.toggle?.addEventListener('click', this.handleToggle)
        this.addEventListener('focusin', this.handleFocusIn)
        if (this.hasAttribute('data-uc-marquee-draggable')) {
            this.rail?.addEventListener('pointerdown', this.handlePointerDown)
            this.rail?.addEventListener('pointermove', this.handlePointerMove)
            window.addEventListener('pointerup', this.handlePointerEnd)
            window.addEventListener('pointercancel', this.handlePointerEnd)
            this.rail?.addEventListener('lostpointercapture', this.handlePointerEnd)
            this.rail?.addEventListener('click', this.handleClick, true)
            this.rail?.addEventListener('dragstart', this.handleDragStart)
        }
        this.motionPreference.addEventListener('change', this.handleMotionPreference)
        this.updateLoopWidth()
        this.resizeObserver.observe(this.textWrapper)
        this.observer.observe(this)
    }

    private startMarquee(): void {
        if (
            this.animation !== null ||
            this.paused ||
            this.drag ||
            this.motionPreference.matches ||
            !this.isIntersecting
        ) {
            return
        }

        this.animation = requestAnimationFrame(this.moveItems.bind(this))
    }

    private moveItems() {
        this.currentPosition -= this.speed

        if (this.loopWidth > 0 && -this.currentPosition >= this.loopWidth) {
            this.currentPosition += this.loopWidth
        } else if (
            this.loopWidth === 0 &&
            this.textWrapper.getBoundingClientRect().right < window.innerWidth
        ) {
            this.currentPosition = 0
        }

        this.applyPosition()
        this.animation = requestAnimationFrame(this.moveItems.bind(this))
    }

    stopMarquee(reset = true): void {
        if (this.animation !== null) {
            cancelAnimationFrame(this.animation)
            this.animation = null
        }

        if (reset) {
            this.currentPosition = 0
            this.applyPosition()
        }
    }

    private applyPosition(): void {
        this.textWrapper.style.transform = `translateX(${this.currentPosition}px)`
    }

    private handlePointerDown(event: PointerEvent): void {
        if (!event.isPrimary || event.button !== 0 || this.motionPreference.matches) return
        this.suppressClick = false
        this.drag = {
            pointerId: event.pointerId,
            x: event.clientX,
            y: event.clientY,
            position: this.currentPosition,
            active: false,
        }
        this.stopMarquee(false)
    }

    private handlePointerMove(event: PointerEvent): void {
        const drag = this.drag
        if (!drag || drag.pointerId !== event.pointerId) return
        const distance = event.clientX - drag.x
        if (!drag.active) {
            if (Math.abs(distance) < 6) return
            if (Math.abs(event.clientY - drag.y) > Math.abs(distance)) {
                this.handlePointerEnd(event)
                return
            }
            drag.active = true
            this.paused = true
            this.suppressClick = true
            this.updateToggle()
            this.rail?.setPointerCapture(event.pointerId)
            this.rail?.setAttribute('data-uc-dragging', '')
        }
        event.preventDefault()
        this.currentPosition = drag.position + distance
        if (this.loopWidth > 0) {
            this.currentPosition = ((this.currentPosition % this.loopWidth) - this.loopWidth) % this.loopWidth
        }
        this.applyPosition()
    }

    private handlePointerEnd(event: PointerEvent): void {
        if (this.drag?.pointerId !== event.pointerId) return
        this.drag = undefined
        this.rail?.removeAttribute('data-uc-dragging')
        if (this.rail?.hasPointerCapture(event.pointerId)) {
            this.rail.releasePointerCapture(event.pointerId)
        }
        this.startMarquee()
    }

    private handleClick(event: MouseEvent): void {
        if (!this.suppressClick || event.detail === 0) return
        event.preventDefault()
        event.stopPropagation()
        this.suppressClick = false
    }

    private handleDragStart(event: DragEvent): void {
        event.preventDefault()
    }

    private updateLoopWidth(): void {
        if (this.copyStarts.length < 2) return
        this.loopWidth =
            this.copyStarts[1].offsetLeft - this.copyStarts[0].offsetLeft
    }

    private handleToggle(): void {
        this.paused = !this.paused
        this.updateToggle()

        if (this.paused) {
            this.stopMarquee(false)
        } else {
            this.startMarquee()
        }
    }

    private updateToggle(): void {
        if (!this.toggle) return
        const state = this.paused ? 'paused' : 'playing'
        const label = this.paused
            ? this.toggle.dataset.playLabel
            : this.toggle.dataset.pauseLabel

        this.toggle.dataset.state = state
        if (label) this.toggle.setAttribute('aria-label', label)
    }

    private handleFocusIn(event: FocusEvent): void {
        const target = q.ot<FocusEvent>(event)
        const item = target?.closest<HTMLElement>('[data-uc-marquee-item]')
        const firstCopy = this.copyStarts[0]
        if (!item || !firstCopy || !this.toggle || this.motionPreference.matches) return

        this.paused = true
        this.stopMarquee(false)
        this.updateToggle()
        this.rail?.scrollTo({ left: 0 })
        this.currentPosition = firstCopy.offsetLeft - item.offsetLeft

        if (this.loopWidth > 0) {
            this.currentPosition %= this.loopWidth
        }

        this.applyPosition()
    }

    private handleMotionPreference(): void {
        if (this.motionPreference.matches) {
            this.stopMarquee()
        } else {
            this.startMarquee()
        }
    }

    private handleIntersection(entries: IntersectionObserverEntry[]): void {
        entries.forEach((entry) => {
            this.isIntersecting = entry.isIntersecting
            if (entry.isIntersecting) {
                this.startMarquee()
            } else {
                this.stopMarquee(!this.paused)
            }
        })
    }

    override disconnectedCallback(): void {
        this.stopMarquee(false)
        this.observer.disconnect()
        this.resizeObserver.disconnect()
        this.toggle?.removeEventListener('click', this.handleToggle)
        this.removeEventListener('focusin', this.handleFocusIn)
        this.rail?.removeEventListener('pointerdown', this.handlePointerDown)
        this.rail?.removeEventListener('pointermove', this.handlePointerMove)
        window.removeEventListener('pointerup', this.handlePointerEnd)
        window.removeEventListener('pointercancel', this.handlePointerEnd)
        this.rail?.removeEventListener('lostpointercapture', this.handlePointerEnd)
        this.rail?.removeEventListener('click', this.handleClick, true)
        this.rail?.removeEventListener('dragstart', this.handleDragStart)
        this.drag = undefined
        this.motionPreference.removeEventListener('change', this.handleMotionPreference)
        super.disconnectedCallback()
    }
}
